import { FocusEvent, KeyboardEvent, useCallback, useRef } from "react";

import { useAttentionTravel } from "./AttentionTargets";
import { fieldPathOf } from "./fieldPath";
import { type OfferedValue, takeOfferForEvent } from "./offeredValueShortcuts";

/**
 * Which field the caret is in, and which keystroke takes which of its offers.
 *
 * Only the keyboard half of answering "something else has a different value for
 * this field". The chips themselves render inside each field's own box, through
 * `FieldAccessoryProvider` on the manifest form and directly on the extraction
 * grid — not in an overlay anchored to the focused field, which was wrong twice
 * over: painted below the dialog containing it the row was invisible, and on a
 * select it landed exactly where the dropdown opens.
 *
 * The warning icon on the field stays: it is what makes a flagged value findable
 * at a glance, and travel opens it to say *why*. What moved is the acting.
 */

/** What a field is being offered, and how the reviewer can answer. */
export interface FieldOffers<T> {
  offers: readonly OfferedValue<T>[];
  /** Present when there is a flag to clear by keeping what the field holds. */
  canKeep: boolean;
}

export const NO_FIELD_OFFERS: FieldOffers<never> = { offers: [], canKeep: false };

/**
 * Track the focused field and what is on offer for it.
 *
 * `offersFor` is the only thing that differs between surfaces: the manifest form
 * looks up what e-Manifest and vendor data hold, the extraction grid looks up
 * what the record it is about to overwrite holds. Everything else — when the
 * chips appear, which keystroke takes which offer, what keeping means — is the
 * same, which is the point.
 */
export function useFocusedFieldOffers<T>({
  offersFor,
  onTake,
  onKeep,
}: {
  offersFor: (path: string) => FieldOffers<T>;
  onTake: (path: string, offer: OfferedValue<T>) => void;
  onKeep?: (path: string) => void;
}): {
  /**
   * Spread onto whatever contains the fields. Capture phase, so one handler on
   * the form serves every input inside it without a wrapper per field.
   */
  focusProps: {
    onFocusCapture: (event: FocusEvent<HTMLElement>) => void;
    onBlurCapture: (event: FocusEvent<HTMLElement>) => void;
    onKeyDownCapture: (event: KeyboardEvent<HTMLElement>) => void;
  };
} {
  /**
   * A ref, not state: nothing renders from it.
   *
   * Which field the caret is in decides what a keystroke does, and that is
   * decided when the key is pressed. The chips themselves are rendered by each
   * field regardless of focus, and the keystroke badges on them are revealed by
   * `:focus-within` — CSS already knows where the caret is. Holding this in
   * state re-rendered the whole form on every focus move to tell it something
   * nothing on screen was waiting for, which on this form costs a fifth of a
   * second and reads as the click not registering.
   */
  const focused = useRef<{ path: string; element: HTMLElement } | null>(null);

  const onFocusCapture = useCallback((event: FocusEvent<HTMLElement>) => {
    const element = event.target;
    const path = fieldPathOf(element);
    focused.current = path == null ? null : { path, element };
  }, []);

  const onBlurCapture = useCallback((event: FocusEvent<HTMLElement>) => {
    // Scoped to the field being left, so the blur of one field cannot clear what
    // the caret has already moved on to.
    const leaving = fieldPathOf(event.target);
    if (focused.current != null && focused.current.path === leaving) focused.current = null;
  }, []);

  const takeOffer = useCallback(
    (offer: OfferedValue<T>) => {
      const current = focused.current;
      if (current == null) return;
      onTake(current.path, offer);
      // Focus stays in the field: the reviewer took a value mid-transcription and
      // the next thing they do is tab onward, not hunt for where they were.
      current.element.focus();
    },
    [onTake]
  );

  const keep = useCallback(() => {
    const current = focused.current;
    if (current == null) return;
    onKeep?.(current.path);
    current.element.focus();
  }, [onKeep]);

  // Answering from the keyboard hands the reviewer to whatever is next; clicking
  // does not, because a click already put them somewhere deliberately and pulling
  // the caret out from under the pointer is a different gesture.
  const { travelToNextPublished } = useAttentionTravel();
  const takeFromKeyboard = useCallback(
    (offer: OfferedValue<T>) => {
      takeOffer(offer);
      travelToNextPublished();
    },
    [takeOffer, travelToNextPublished]
  );
  const keepFromKeyboard = useCallback(() => {
    keep();
    travelToNextPublished();
  }, [keep, travelToNextPublished]);

  // Resolved when the key is pressed, not during render. What is on offer
  // depends on what the field currently holds — taking one, or typing the value
  // yourself, drops it from the row — and the shortcuts are numbered by position
  // in that row. A list captured at render time goes stale the moment the
  // reviewer types, and `⌥2` then means a different chip than the badge beside
  // it. Resolving on the keystroke also keeps this off the render path, so the
  // form is not subscribed to its own values just to number three chips.
  const onKeyDownCapture = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      const current = focused.current;
      if (current == null) return;
      takeOfferForEvent(
        event,
        offersFor(current.path).offers,
        takeFromKeyboard,
        onKeep == null ? undefined : keepFromKeyboard
      );
    },
    [offersFor, takeFromKeyboard, onKeep, keepFromKeyboard]
  );

  return { focusProps: { onFocusCapture, onBlurCapture, onKeyDownCapture } };
}
